import { Helmet } from 'react-helmet-async';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Card,
  Stack,
  Alert,
  Avatar,
  Button,
  Tooltip,
  Container,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';
import Iconify from './iconify';

const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER_URL;

// GET /api/activity_dates?imei=... vraća redove tabele activity_dates, sortirane po datumu
const ACTIVE_DATES_URL = `${SERVER_URL}/activity_dates`;

// Mapa putanje: IMEI ide kroz localStorage (kao iz ostalih tabela), a datum kroz router state,
// da otvaranje Mape iz drugih stranica ne bi naslijedilo stari datum
const MAP_URL = '/dashboard/HistoryMapa';

const MJESECI = [
  'Januar',
  'Februar',
  'Mart',
  'April',
  'Maj',
  'Juni',
  'Juli',
  'August',
  'Septembar',
  'Oktobar',
  'Novembar',
  'Decembar',
];

const DANI = ['Pon', 'Uto', 'Sri', 'Čet', 'Pet', 'Sub', 'Ned'];

// 'YYYY-MM-DD' bez prolaska kroz UTC (toISOString bi pomjerio datum za dan)
const kljuc = (godina, mjesec, dan) =>
  `${godina}-${String(mjesec + 1).padStart(2, '0')}-${String(dan).padStart(2, '0')}`;

const danasKljuc = () => {
  const d = new Date();
  return kljuc(d.getFullYear(), d.getMonth(), d.getDate());
};

const prikaziDatum = (k) => {
  const [g, m, d] = k.split('-');
  return `${Number(d)}.${Number(m)}.${g}.`;
};

// Odgovor servera -> { 'YYYY-MM-DD': podaciZaDan }
function normalizuj(data) {
  const mapa = {};
  (Array.isArray(data) ? data : []).forEach((red) => {
    const k = String(red?.datum ?? '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(k)) return;
    mapa[k] = {
      datum: k,
      calculated: Number(red.calculated) === 1,
      path_points: Number(red.path_points) || 0,
      path_length: Number(red.path_length) || 0,
      path_ocjena: Number(red.path_ocjena) || 0,
      device_ocjena: Number(red.device_ocjena) || 0,
      path_start_time: red.path_start_time,
      path_end_time: red.path_end_time,
      device_gaps: Number(red.device_gaps) || 0,
      device_max_gap: Number(red.device_max_gap) || 0,
    };
  });
  return mapa;
}

// '2026-09-12T06:40:00.000Z' -> '06:40' (vrijeme je u bazi već lokalno, ne preračunava se)
const satMinuta = (v) => (v ? String(v).replace(' ', 'T').slice(11, 16) : '');

const trajanje = (od, doVremena) => {
  const [h1, m1] = satMinuta(od).split(':').map(Number);
  const [h2, m2] = satMinuta(doVremena).split(':').map(Number);
  const min = h2 * 60 + m2 - (h1 * 60 + m1);
  if (!Number.isFinite(min) || min <= 0) return '';
  return `${Math.floor(min / 60)} h ${min % 60} min`;
};

const formatKm = (km) => `${km.toFixed(1).replace('.', ',')} km`;

// Boja za ocjenu 0–100
const bojaZaOcjenu = (ocjena) => {
  if (ocjena >= 80) return 'success';
  if (ocjena >= 50) return 'warning';
  return 'error';
};

// Boja šape prema ocjeni rada uređaja (ako je dan izračunat)
const bojaOcjene = (dan) => (dan?.calculated ? `${bojaZaOcjenu(dan.device_ocjena)}.main` : 'primary.main');

// Mala oznaka ocjene u ćeliji kalendara, npr. "U 85"
function OcjenaPill({ slovo, ocjena, naziv }) {
  const boja = bojaZaOcjenu(ocjena);
  return (
    <Tooltip title={`${naziv}: ${ocjena} / 100`}>
      <Box
        component="span"
        sx={{
          px: 0.6,
          borderRadius: 0.75,
          fontSize: 11,
          fontWeight: 700,
          lineHeight: '18px',
          color: `${boja}.dark`,
          bgcolor: (theme) => alpha(theme.palette[boja].main, 0.16),
        }}
      >
        {slovo} {ocjena}
      </Box>
    </Tooltip>
  );
}

// Red s ikonicom i tekstom u ćeliji kalendara
function CelijaRed({ ikona, children }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
      <Iconify icon={ikona} width={13} sx={{ color: 'text.secondary', flexShrink: 0 }} />
      <Typography variant="caption" noWrap sx={{ lineHeight: 1.3 }}>
        {children}
      </Typography>
    </Stack>
  );
}

function Stavka({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="subtitle1">{value}</Typography>
    </Box>
  );
}

// ----------------------------------------------------------------------
// Kalendar aktivnih dana jednog uređaja. Uređaj dolazi iz localStorage
// (postavlja ga dugme History u tabeli uređaja).

export default function HistoryActiveDates() {
  const navigate = useNavigate();

  const uredjaj = useMemo(
    () => ({
      id: localStorage.getItem('id'),
      IMEI: localStorage.getItem('IMEI'),
      ime: localStorage.getItem('ime'),
      tip: localStorage.getItem('tip'),
      tiptrackera: localStorage.getItem('tiptrackera'),
      onlinetoday: Number(localStorage.getItem('onlinetoday')) > 0,
      vlasnik: `${localStorage.getItem('subscriberime') ?? ''} ${localStorage.getItem('subscriberprezime') ?? ''}`.trim(),
    }),
    []
  );

  const [aktivniDani, setAktivniDani] = useState({});
  const [status, setStatus] = useState('loading'); // loading | ok | greska | nema-uredjaja
  const [izabraniDan, setIzabraniDan] = useState(null);
  const [prikaz, setPrikaz] = useState(() => {
    const d = new Date();
    return { godina: d.getFullYear(), mjesec: d.getMonth() };
  });

  // --- Učitavanje aktivnih dana ---

  useEffect(() => {
    if (!uredjaj.IMEI) {
      setStatus('nema-uredjaja');
      return;
    }

    const ucitaj = async () => {
      try {
        const response = await axios.get(`${ACTIVE_DATES_URL}?imei=${uredjaj.IMEI}`);
        const mapa = normalizuj(response.data);

        // Današnji dan se u activity_dates upisuje tek u noćnoj obradi
        if (uredjaj.onlinetoday && !mapa[danasKljuc()]) {
          mapa[danasKljuc()] = { datum: danasKljuc(), calculated: false, danas: true };
        }

        setAktivniDani(mapa);

        // Kalendar otvori na mjesecu zadnje aktivnosti
        const zadnji = Object.keys(mapa).sort().pop();
        if (zadnji) {
          const [g, m] = zadnji.split('-');
          setPrikaz({ godina: Number(g), mjesec: Number(m) - 1 });
        }
        setStatus('ok');
      } catch (err) {
        console.log('Greška pri učitavanju aktivnih dana', err);
        setStatus('greska');
      }
    };

    ucitaj();
  }, [uredjaj]);

  // --- Izračun za prikazani mjesec ---

  const sviDani = useMemo(() => Object.keys(aktivniDani).sort(), [aktivniDani]);

  const celije = useMemo(() => {
    const { godina, mjesec } = prikaz;
    const prvi = new Date(godina, mjesec, 1);
    const pomak = (prvi.getDay() + 6) % 7; // sedmica počinje ponedjeljkom
    const brojDana = new Date(godina, mjesec + 1, 0).getDate();

    const lista = Array.from({ length: pomak }, () => null);
    for (let dan = 1; dan <= brojDana; dan += 1) lista.push(kljuc(godina, mjesec, dan));
    while (lista.length % 7) lista.push(null);
    return lista;
  }, [prikaz]);

  const daniUMjesecu = celije.filter((k) => k && aktivniDani[k]).map((k) => aktivniDani[k]);
  const aktivnihUMjesecu = daniUMjesecu.length;
  const kmUMjesecu = daniUMjesecu.reduce((zbir, d) => zbir + (d.calculated ? d.path_length : 0), 0);
  const dan = izabraniDan ? aktivniDani[izabraniDan] : null;

  const promijeniMjesec = (korak) => {
    setPrikaz(({ godina, mjesec }) => {
      const d = new Date(godina, mjesec + korak, 1);
      return { godina: d.getFullYear(), mjesec: d.getMonth() };
    });
  };

  const prikaziMapu = (datum) => {
    localStorage.setItem('IMEI', uredjaj.IMEI);
    navigate(MAP_URL, { state: { datum } });
  };

  const idiNaDatum = (k) => {
    if (!k) return;
    const [g, m] = k.split('-');
    setPrikaz({ godina: Number(g), mjesec: Number(m) - 1 });
  };

  const danas = danasKljuc();

  // --- Render ---

  return (
    <>
      <Helmet>
        <title> History | GPS Dogs Tracking System </title>
      </Helmet>

      <Container maxWidth={false}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Button startIcon={<Iconify icon="mdi:arrow-left" />} onClick={() => navigate(-1)}>
            Nazad
          </Button>
          <Typography variant="subtitle2">Aktivni dani uređaja</Typography>
        </Stack>

        <Card sx={{ p: 3 }}>
          {status === 'nema-uredjaja' ? (
            <Alert severity="warning">
              Nije izabran uređaj. Otvorite ovu stranicu preko dugmeta History u tabeli uređaja.
            </Alert>
          ) : (
            <>
              {/* Zaglavlje: uređaj */}
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <Avatar
                  src={uredjaj.tip ? `/assets/images/lovackipsi/icon_${uredjaj.tip}.png` : undefined}
                  sx={{ width: 56, height: 56 }}
                  variant="rounded"
                >
                  <Iconify icon="mdi:paw" width={30} />
                </Avatar>
                <Box>
                  <Typography variant="h6">{uredjaj.ime}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {uredjaj.vlasnik && `${uredjaj.vlasnik} · `}
                    {uredjaj.tiptrackera} · IMEI {uredjaj.IMEI}
                  </Typography>
                </Box>
              </Stack>

              {status === 'loading' && (
                <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2">Učitavam aktivne dane...</Typography>
                </Stack>
              )}

              {status === 'greska' && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Aktivni dani nisu učitani. Provjerite da li server ima endpoint za aktivne dane i pokušajte ponovo.
                </Alert>
              )}

              {status === 'ok' && (
                <>
                  {/* Navigacija po mjesecima */}
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <IconButton onClick={() => promijeniMjesec(-1)} aria-label="Prethodni mjesec">
                      <Iconify icon="mdi:chevron-left" width={28} />
                    </IconButton>

                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5">
                        {MJESECI[prikaz.mjesec]} {prikaz.godina}.
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {aktivnihUMjesecu === 0
                          ? 'Nema aktivnosti u ovom mjesecu'
                          : `Aktivnih dana u mjesecu: ${aktivnihUMjesecu}${
                              kmUMjesecu > 0 ? ` · ${formatKm(kmUMjesecu)}` : ''
                            }`}
                      </Typography>
                    </Box>

                    <IconButton onClick={() => promijeniMjesec(1)} aria-label="Sljedeći mjesec">
                      <Iconify icon="mdi:chevron-right" width={28} />
                    </IconButton>
                  </Stack>

                  {/* Nazivi dana */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: 1 }}>
                    {DANI.map((d, i) => (
                      <Typography
                        key={d}
                        variant="caption"
                        sx={{ textAlign: 'center', color: i >= 5 ? 'primary.main' : 'text.secondary', fontWeight: 600 }}
                      >
                        {d}
                      </Typography>
                    ))}
                  </Box>

                  {/* Dani */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
                    {celije.map((k, i) => {
                      if (!k) return <Box key={`prazno-${i}`} />;

                      const podaci = aktivniDani[k];
                      const aktivan = Boolean(podaci);
                      const jeDanas = k === danas;
                      const izabran = k === izabraniDan;
                      const buducnost = k > danas;

                      const celija = (
                        <Box
                          onClick={aktivan ? () => setIzabraniDan(izabran ? null : k) : undefined}
                          sx={{
                            cursor: aktivan ? 'pointer' : 'default',
                            minHeight: { xs: 56, md: 118 },
                            p: 0.75,
                            borderRadius: 1.5,
                            border: (theme) =>
                              izabran || jeDanas
                                ? `2px solid ${theme.palette.primary.main}`
                                : `1px solid ${alpha(theme.palette.grey[500], 0.24)}`,
                            bgcolor: (theme) =>
                              izabran
                                ? alpha(theme.palette.primary.main, 0.2)
                                : aktivan
                                ? alpha(theme.palette.primary.main, 0.08)
                                : 'background.paper',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            overflow: 'hidden',
                          }}
                        >
                          {/* Broj dana i šapa */}
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: aktivan ? 700 : 400,
                                color: buducnost ? 'text.disabled' : 'text.primary',
                              }}
                            >
                              {Number(k.slice(8))}
                            </Typography>
                            {aktivan && <Iconify icon="mdi:paw" width={22} sx={{ color: bojaOcjene(podaci) }} />}
                          </Stack>

                          {/* Podaci dana (na mobitelu samo šapa, detalji na klik) */}
                          {aktivan && podaci.calculated && (
                            <Stack spacing={0.25} sx={{ display: { xs: 'none', md: 'flex' }, minWidth: 0 }}>
                              <CelijaRed ikona="mdi:clock-outline">
                                {satMinuta(podaci.path_start_time)}–{satMinuta(podaci.path_end_time)}
                              </CelijaRed>
                              <CelijaRed ikona="mdi:map-marker-multiple-outline">
                                {podaci.path_points} lok.
                              </CelijaRed>
                              <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
                                <OcjenaPill slovo="U" ocjena={podaci.device_ocjena} naziv="Ocjena uređaja" />
                                <OcjenaPill slovo="P" ocjena={podaci.path_ocjena} naziv="Ocjena putanje" />
                              </Stack>
                            </Stack>
                          )}

                          {aktivan && !podaci.calculated && (
                            <Typography
                              variant="caption"
                              sx={{ display: { xs: 'none', md: 'block' }, color: 'text.disabled', lineHeight: 1.3 }}
                            >
                              {podaci.danas ? 'Aktivan danas' : 'Nije obrađeno'}
                            </Typography>
                          )}
                        </Box>
                      );

                      return aktivan ? (
                        <Tooltip
                          key={k}
                          arrow
                          title={
                            podaci.calculated
                              ? `${prikaziDatum(k)} · ${podaci.path_points} lokacija · ${formatKm(podaci.path_length)}`
                              : `${prikaziDatum(k)}${podaci.danas ? ' · aktivan danas' : ''}`
                          }
                        >
                          {celija}
                        </Tooltip>
                      ) : (
                        <Box key={k}>{celija}</Box>
                      );
                    })}
                  </Box>

                  {/* Legenda */}
                  <Stack
                    direction="row"
                    spacing={2}
                    flexWrap="wrap"
                    sx={{ mt: 1.5, display: { xs: 'none', md: 'flex' }, color: 'text.secondary' }}
                  >
                    <Typography variant="caption">U = ocjena uređaja</Typography>
                    <Typography variant="caption">P = ocjena putanje</Typography>
                    <Typography variant="caption">
                      Boja šape: zelena 80+, oker 50–79, crvena ispod 50
                    </Typography>
                  </Stack>

                  {/* Detalji izabranog dana */}
                  {dan && (
                    <Box
                      sx={{
                        mt: 3,
                        p: 2,
                        borderRadius: 1.5,
                        bgcolor: 'background.neutral',
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                        <Typography variant="subtitle1">{prikaziDatum(dan.datum)}</Typography>
                        <IconButton size="small" onClick={() => setIzabraniDan(null)} aria-label="Zatvori">
                          <Iconify icon="mdi:close" />
                        </IconButton>
                      </Stack>

                      {dan.calculated ? (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                            gap: 2,
                          }}
                        >
                          <Stavka label="Lokacija" value={dan.path_points} />
                          <Stavka label="Pređeno" value={formatKm(dan.path_length)} />
                          <Stavka label="Ocjena uređaja" value={`${dan.device_ocjena} / 100`} />
                          <Stavka label="Ocjena putanje" value={`${dan.path_ocjena} / 100`} />
                          <Stavka label="Početak" value={satMinuta(dan.path_start_time)} />
                          <Stavka label="Kraj" value={satMinuta(dan.path_end_time)} />
                          <Stavka
                            label="Trajanje"
                            value={trajanje(dan.path_start_time, dan.path_end_time) || '-'}
                          />
                          <Stavka
                            label="Prekidi"
                            value={
                              dan.device_gaps > 0
                                ? `${dan.device_gaps} (najduži ${dan.device_max_gap} min)`
                                : 'Bez prekida'
                            }
                          />
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {dan.danas
                            ? 'Uređaj je danas aktivan. Podaci za današnji dan biće izračunati u noćnoj obradi.'
                            : 'Detaljni podaci za ovaj dan još nisu izračunati.'}
                        </Typography>
                      )}

                      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                        <Button
                          variant="contained"
                          startIcon={<Iconify icon="mdi:map-marker-path" />}
                          onClick={() => prikaziMapu(dan.datum)}
                        >
                          Show Map
                        </Button>
                      </Stack>
                    </Box>
                  )}

                  {/* Sažetak */}
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1, sm: 3 }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    sx={{ mt: 3, pt: 2, borderTop: (theme) => `1px dashed ${alpha(theme.palette.grey[500], 0.32)}` }}
                  >
                    <Typography variant="body2">
                      Ukupno aktivnih dana: <strong>{sviDani.length}</strong>
                    </Typography>
                    {sviDani.length > 0 && (
                      <>
                        <Button size="small" onClick={() => idiNaDatum(sviDani[0])}>
                          Prva aktivnost: {prikaziDatum(sviDani[0])}
                        </Button>
                        <Button size="small" onClick={() => idiNaDatum(sviDani[sviDani.length - 1])}>
                          Zadnja aktivnost: {prikaziDatum(sviDani[sviDani.length - 1])}
                        </Button>
                      </>
                    )}
                  </Stack>
                </>
              )}
            </>
          )}
        </Card>
      </Container>
    </>
  );
}